/**
 * Les assurances obligatoires du micro-entrepreneur.
 *
 * LE STATUT NE CRÉE AUCUNE OBLIGATION ET N'EN DISPENSE D'AUCUNE. C'est
 * l'activité exercée qui décide, jamais la forme juridique ni le montant
 * encaissé. Un plaquiste à 8 000 € de chiffre d'affaires doit sa décennale
 * comme une entreprise de cinquante salariés.
 *
 * CINQ OBLIGATIONS, ET AUCUNE NE SE DEVINE :
 *   - la GARANTIE DÉCENNALE pour les professions du bâtiment ;
 *   - la RESPONSABILITÉ CIVILE PROFESSIONNELLE pour les activités réglementées ;
 *   - l'assurance de CHAQUE VÉHICULE utilisé à titre professionnel ;
 *   - l'assurance d'un LOCAL LOUÉ, sur quatre risques nommés ;
 *   - la complémentaire santé des SALARIÉS, à moitié à la charge de l'employeur.
 * Les trois dernières sont celles qu'un diagnostic oublie, parce qu'on les croit
 * couvertes ailleurs.
 *
 * LA LISTE DES ACTIVITÉS RÉGLEMENTÉES N'EST PAS CLOSE, et c'est le point le plus
 * délicat du sujet. La page officielle cite des exemples et renvoie à l'annuaire
 * de l'INPI. Ce module ne prétend donc JAMAIS qu'une activité absente d'une
 * liste n'est pas concernée : il demande au lecteur si la sienne est
 * réglementée, et lui dit où le vérifier. Publier une liste close ferait
 * conclure à tort à qui ne s'y trouve pas.
 *
 * POUR TOUT LE RESTE, aucun texte n'impose la RC professionnelle. L'obligation
 * revient par une autre porte,
 * celle du contrat : agences, grands comptes, collectivités et appels d'offres
 * la réclament avant de signer.
 *
 * DEUX ANGLES MORTS valent pour tous les contrats, et le module les rappelle :
 * seules les activités DÉCLARÉES à l'assureur sont couvertes, et une réclamation
 * peut arriver longtemps après la livraison, quand le contrat ne joue plus.
 */

/** D'où viennent les règles, et quand elles ont été relevées. */
export const BAREME = {
    annee: "2026",
    releveLe: "2026-09-08",
    source: "service-public.gouv.fr, assurances du micro-entrepreneur, vérifiée le 7 juillet 2026",
};

/** Les quatre risques que doit couvrir l'assurance d'un local loué. */
export const RISQUES_LOCAL = [
    "incendie",
    "explosion",
    "dégâts des eaux",
    "catastrophes naturelles",
];

/** La part minimale de la complémentaire santé à la charge de l'employeur. */
export const PART_PATRONALE_MINIMALE = 50;

/** Où vérifier si une activité est réglementée. Aucune liste close n'existe. */
export const ANNUAIRE_REGLEMENTEES = "l'annuaire des activités et professions réglementées de l'INPI";

/**
 * Les obligations que le parcours sait nommer, chacune avec son fondement.
 *
 * L'ordre est celui de la gravité : la décennale d'abord, parce que son défaut
 * est un délit et qu'elle se souscrit AVANT le chantier.
 */
export const OBLIGATIONS = [
    {
        cle: "decennale",
        question: "batiment",
        fondement: "articles 1792 du code civil et L. 241-1 du code des assurances",
    },
    {
        cle: "rcPro",
        question: "reglementee",
        fondement: "code des assurances et textes propres à chaque profession réglementée",
    },
    {
        cle: "vehicule",
        question: "vehicule",
        fondement: "obligation d'assurance de tout véhicule utilisé à titre professionnel",
    },
    {
        cle: "local",
        question: "local",
        fondement: "obligation d'assurance du locataire d'un local professionnel",
    },
    {
        cle: "salaries",
        question: "salaries",
        fondement: "protection sociale complémentaire des salariés",
    },
];

/** Les questions du parcours, dans l'ordre où elles se posent. */
export const QUESTIONS = OBLIGATIONS.map((o) => o.question);

/* --------------------------------------------------------------------- */
/* Le diagnostic                                                          */
/* --------------------------------------------------------------------- */

/** Une réponse est-elle un oui franc ? */
function oui(valeur) {
    return valeur === "oui";
}

/**
 * Ce qui s'impose, au vu des réponses.
 *
 * Rend `null` tant qu'une question reste sans réponse : un diagnostic partiel
 * annoncerait moins d'obligations qu'il n'y en a, ce qui est le pire des
 * résultats sur ce sujet.
 */
export function obligations(reponses) {
    const r = reponses || {};
    if (QUESTIONS.some((q) => !r[q])) return null;

    const dues = OBLIGATIONS.filter((o) => oui(r[o.question]));

    return {
        dues,
        cles: dues.map((o) => o.cle),
        aucune: dues.length === 0,
        // La décennale commande le reste : son défaut est un délit, et elle se
        // souscrit avant l'ouverture du chantier, pas après la signature.
        decennale: dues.some((o) => o.cle === "decennale"),
        // DEUX RÈGLES, PAS UNE. L'attestation de décennale se joint à chaque
        // devis et à chaque facture (Service-Public F2034). Le nom de
        // l'assureur, ses coordonnées et la couverture géographique ne
        // s'écrivent sur les devis et factures qu'en cas d'activité artisanale
        // soumise à une assurance obligatoire (code de l'artisanat, L. 132-1 et
        // R. 132-1 ; F31808). Jusqu'au 25 septembre 2026, le module les exigeait
        // de toute activité réglementée : un avocat ou une infirmière n'y sont
        // pas tenus par ce texte. Le parcours ne sait pas si l'activité est
        // artisanale : il le dit sous condition.
        attestationDecennale: dues.some((o) => o.cle === "decennale"),
        mentionsSiArtisanale: dues.some((o) => o.cle === "decennale" || o.cle === "rcPro"),
        // Aucun texte n'impose la RC pro à une activité non réglementée : c'est
        // le contrat qui la réclamera, et il faut le dire plutôt que de laisser
        // croire à une obligation qui n'existe pas.
        rcProParContrat: !oui(r.reglementee) && !oui(r.batiment),
    };
}

/** Ce qui manque pour trancher. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = QUESTIONS.filter((q) => !r[q]);
    return { valide: manques.length === 0, manques, invalides: [], alertes: [] };
}

/** Ce que le parcours annonce, ligne par ligne. */
export function recapitulatif(reponses) {
    const etat = obligations(reponses);
    if (!etat) return null;

    if (etat.aucune) {
        return [
            [
                "Assurance imposée par un texte",
                "aucune, au vu de vos réponses",
            ],
            [
                "Ce qui revient par le contrat",
                "vos clients peuvent l'exiger, et beaucoup le font",
            ],
        ];
    }

    const libelles = {
        decennale: ["Garantie décennale", "obligatoire, et à souscrire avant l'ouverture du chantier"],
        rcPro: ["Responsabilité civile professionnelle", "obligatoire pour une activité réglementée"],
        vehicule: ["Véhicule professionnel", "chaque véhicule utilisé pour l'activité doit être assuré"],
        local: ["Local loué", `à couvrir contre ${RISQUES_LOCAL.join(", ")}`],
        salaries: [
            "Complémentaire santé des salariés",
            `à votre charge pour ${PART_PATRONALE_MINIMALE} % au moins`,
        ],
    };

    const lignes = etat.cles.map((cle) => libelles[cle]);
    if (etat.attestationDecennale) {
        lignes.push(["Sur vos devis et factures", "l'attestation de votre garantie décennale, jointe à chacun"]);
    }
    if (etat.mentionsSiArtisanale) {
        lignes.push([
            "Si votre activité est artisanale",
            "vos devis et factures portent aussi le nom de votre assureur, ses coordonnées et la couverture géographique du contrat",
        ]);
    }
    return lignes;
}
